import type { Enums } from "@msuite/utils/types";
import { cva } from "class-variance-authority";
import clsx from "clsx";
import { memo } from "react";
import { Tooltip, type TooltipProps } from "../tooltip";
import type { ClassNameProps } from "../types";
import { getSupabaseIconName } from "./get-supabase-icon";
import { icon as _icon, type IconType } from "./icons";

/** Props Interface */
interface ColoredIconBaseProps {
	color: Enums<"color"> | undefined;
	tooltip?: TooltipProps["label"];
	tooltipOpenDelay?: number;
	withTooltip?: boolean;
}

interface ColoredIconWithName extends ColoredIconBaseProps {
	iconName: Enums<"iconName"> | undefined;
	icon?: never;
}

interface ColoredIconWithNode extends ColoredIconBaseProps {
	icon: IconType;
	iconName?: never;
}

type ColoredIconProps = (ColoredIconWithName | ColoredIconWithNode) & ClassNameProps;

export const ColoredIcon = memo(function ColoredIcon({
	iconName = "arrows-random",
	icon,
	color = "gray",
	tooltip,
	tooltipOpenDelay = 250,
	withTooltip = true,
	className,
}: ColoredIconProps) {
	const isLoading = !iconName || !color;
	const classes = iconClasses({ color, isLoading });
	const Icon = icon || _icon(getSupabaseIconName(iconName));

	if (!withTooltip) {
		return <Icon className={classes} />;
	}

	return (
		<Tooltip
			label={tooltip ?? ""}
			isDisabled={!tooltip}
			openDelay={tooltipOpenDelay}
		>
			<div className={clsx("inline-block", className)}>
				<Icon className={classes} />
			</div>
		</Tooltip>
	);
});

const iconClasses = cva([], {
	variants: {
		color: {
			emerald: ["text-emerald-500"],
			rose: ["text-rose-500"],
			red: ["text-red-500"],
			violet: ["text-violet-500"],
			purple: ["text-purple-500"],
			indigo: ["text-indigo-500"],
			gray: ["text-gray-500"],
			teal: ["text-teal-500"],
			amber: ["text-amber-500"],
			yellow: ["text-yellow-500"],
			orange: ["text-orange-500"],
			blue: ["text-blue-500"],
			primary: ["text-primary-500"],
			cyan: ["text-cyan-500"],
			lime: ["text-lime-500"],
			salmon: ["text-salmon-500"],
			fern: ["text-fern-500"],
			green: ["text-green-500"],
			azure: ["text-azure-500"],
			sky: ["text-sky-500"],
			jade: ["text-jade-500"],
			slate: ["text-slate-500"],
			zinc: ["text-zinc-500"],
			stone: ["text-stone-500"],
			"ember-blaze": ["text-ember-blaze-500"],
			accent: ["text-accent-500"],
		},
		isLoading: {
			true: ["animate-pulse"],
			false: [],
		},
	},
});
